import React, { useCallback } from 'react';
import SizeIconClassic from '../application-title/icons/SizeIconClassic';
import SizeIconLarge from '../application-title/icons/SizeIconLarge';
import SizeIconXL from '../application-title/icons/SizeIconXL';
import Tooltip from '../tooltip';
import './SizeToggle.less';

export type SizeType = string;

const sizeIcons: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  CLASSIC: SizeIconClassic,
  LARGE: SizeIconLarge,
  EXTRA_LARGE: SizeIconXL,
};

interface SizeToggleProps<T extends string = string> {
  currentSize: T;
  availableSizes: T[];
  onSizeChange: (size: T) => void;
  className?: string;
  ariaLabel?: string;
}

export default function SizeToggle<T extends string = string>({
  currentSize,
  availableSizes,
  onSizeChange,
  className = '',
  ariaLabel = 'Toggle application size',
}: SizeToggleProps<T>) {
  const handleSizeChange = useCallback(() => {
    if (!availableSizes || availableSizes.length === 0) return;

    const currentIndex = availableSizes.indexOf(currentSize);
    const nextIndex = (currentIndex + 1) % availableSizes.length;
    const nextSize = availableSizes[nextIndex];

    if (availableSizes.includes(nextSize)) {
      onSizeChange(nextSize);
    }
  }, [availableSizes, currentSize, onSizeChange]);

  if (!currentSize || !availableSizes.includes(currentSize)) {
    return null;
  }

  return (
    <div className={`size-buttons-container ${className}`}>
      <Tooltip
        tooltipContent="Change screen size"
        placement="bottom"
        className="size-toggle-tooltip"
      >
        <button
          onClick={handleSizeChange}
          className={`size-toggle-button size-${availableSizes.indexOf(currentSize) + 1}`}
          aria-label={ariaLabel}
          type="button"
        >
          <div className="switch-track"></div>

          <div className="switch-thumb"></div>

          <div className="icons-container">
            {availableSizes.map((size) => {
              const Icon = sizeIcons[size];
              if (!Icon) {
                return null;
              }

              return (
                <Icon
                  key={size}
                  className={`size-icon ${currentSize === size ? 'active' : ''}`}
                  aria-hidden="true"
                />
              );
            })}
          </div>
        </button>
      </Tooltip>
    </div>
  );
}
